<script setup>
import { ref } from 'vue';
import OfficeIFrame from './components/OfficeIFrame.vue'
import Sidebar from './components/Sidebar.vue'
import { onBeforeMount } from 'vue';
import { provide } from 'vue';
import { getUserId } from './utils/auth';
import SidebarMobile from './components/SidebarMobile.vue';
import { setUser } from '@sentry/vue';

const sidebar = ref(null);
const presentationId = ref('');

const isLoading = ref(false);
const isModalVisible = ref(false);
const isModalImageVeryTall = ref(false);
const editorIframeRef = ref(null);
const modalImage = ref(null);
const isSignUpVisible = ref(false);
const user = ref({});
const isMobile = ref();
const isDocumentLoaded = ref(false);

onBeforeMount(async () => {
  const userData = await getUserId();
  user.value.id = userData.user_id;
  user.value.is_paid = userData.paid;
  setUser({ id: user.value.id });

  const parts = window.location.pathname.split('/');
  presentationId.value = parts[parts.length - 1];
  if (presentationId.value === '') {
    presentationId.value = 'aGVsbG8gd29ybGQub2Rw';
  }
});

const setEditorIframeRef = (ref) => {
  editorIframeRef.value = ref;
};

provide('userRef', user);
provide('editorIframeRef', {
  setEditorIframeRef,
  editorIframeRef
});
provide('isMobileRef', isMobile);
provide('isDocumentLoadedRef', isDocumentLoaded);


function toggleSidebar(sidebarId) {
  sidebar.value.toggleSidebar(sidebarId)
}

function toggleIsLoading(state) {
  isLoading.value = state;
}

function openModal(imageSrc) {
  // Load the image first to check its dimensions
  const img = new Image();
  img.onload = function () {
    modalImage.value.src = imageSrc;

    // Determine if the image is portrait and very tall
    const isPortrait = img.height > img.width;
    const isVeryTall = img.height > window.innerHeight * 2;

    // Apply appropriate class
    if (isPortrait && isVeryTall) {
      isModalImageVeryTall.value = true;
    } else {
      isModalImageVeryTall.value = false;
    }
  };
  img.src = imageSrc;
  isModalVisible.value = true;
}

function closeModal() {
  isModalVisible.value = false;
  modalImage.value = '';
}

function requireAuth() {
  isSignUpVisible.value = true;
}

function redirectToSignUp(url) {
  parent.postMessage({
    "MessageId": "Redirect_To_SignUp",
    "SendTime": Date.now(),
    "Values": {},
    "Origin": location.origin,
  }, "*");
}

</script>

<template>
  <main data-theme="light">
    <OfficeIFrame
      v-if="presentationId"
      :file="file"
      :presentationId="presentationId"
      @toggle-sidebar="toggleSidebar"
      @toggle-loading="toggleIsLoading"
      @require-auth="requireAuth"
    />
    <Sidebar
      ref="sidebar"
      v-if="presentationId && isMobile === false"
      :presentationId="presentationId"
      @image-clicked="openModal"
    />
    <SidebarMobile ref="sidebarMobile" v-else-if="presentationId && isMobile === true" :presentationId="presentationId" />
    <div class="modal" :class="{ 'is-active': isLoading }">
      <div class="modal-background"></div>
      <div class="modal-content">
        <div class="box has-text-centered">
          <p class="is-size-5">Applying changes to the slide content</p>
          <progress class="progress is-small is-primary my-4" max="100">100%</progress>
        </div>
      </div>
    </div>
    <div class="modal" :class="{ 'is-active': isModalVisible }">
      <div class="modal-background" @click="closeModal"></div>
      <div class="modal-content is-clipped" style="width:90vw">
        <p class="image">
          <img ref="modalImage" alt="Selected" class="responsive-image"
               :class="isModalImageVeryTall ? 'is-tall' : ''" />
        </p>
      </div>
      <button class="modal-close is-large" aria-label="close" @click="closeModal"></button>
    </div>
    <Dialog v-model:visible="isSignUpVisible" modal :closable="false" :closeOnEscape="false" header="You're Reading a Preview">
      <div class="is-flex is-justify-content-center">
        <span>Unlock full access with sign up</span>
      </div>
      <div class="is-flex is-justify-content-center pt-4">
        <Button class="has-text-weight-bold" style="background-image: radial-gradient(circle at left top, #E100FF, #7F00FF)"
                @click="redirectToSignUp()">
          Sign Up Now
        </Button>
      </div>
    </Dialog>
  </main>
</template>

<style>
 html,
 body {
   margin: 0px;
   padding: 0px;
   border: none;
   overflow-y: hidden;
 }

 .responsive-image {
   max-width: 100%;
   /* Make sure the width doesn't exceed the modal width */
   max-height: 100vh;
   /* Ensure the image doesn't exceed the viewport height */
   height: auto;
   /* Maintain the image's original aspect ratio */
   object-fit: contain;
   /* Ensure the full image is visible within its bounds */
 }

 p.image:has(.responsive-image.is-tall) {
   overflow-y: scroll;
   height: 90vh;
 }

 .responsive-image.is-tall {
   max-height: unset;
 }
</style>
